// =============================================================================
// AGC: DROID FARM - NDVI Map Overlay
// AGC: Leaflet-compatible NDVI heatmap overlay with opacity control
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react'
import { Layers, Eye, EyeOff, Calendar, RefreshCw } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { NdviOverlayConfig } from '../../../types'

interface NDVIMapOverlayProps {
  config: NdviOverlayConfig | null
  onOpacityChange?: (opacity: number) => void
  onDateChange?: (date: string) => void
  onRefresh?: () => Promise<void>
  onToggle?: (visible: boolean) => void
  isVisible?: boolean
  isLoading?: boolean
  availableDates?: string[]
}

/**
 * AGC: NDVI Color Legend
 */
const NDVILegend: React.FC = () => {
  const { t } = useTranslation()
  
  const legendItems = [
    { color: '#8b0000', label: '< 0', description: t('fields.monitoring.water') },
    { color: '#d2691e', label: '0.0', description: t('fields.monitoring.bareSoil') },
    { color: '#daa520', label: '0.2', description: t('fields.monitoring.sparse') },
    { color: '#9acd32', label: '0.4', description: t('fields.monitoring.moderate') },
    { color: '#228b22', label: '0.6', description: t('fields.monitoring.healthy') },
    { color: '#006400', label: '0.8+', description: t('fields.monitoring.dense') },
  ]

  return (
    <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {t('fields.monitoring.ndviLegend')}
      </p>
      <div className="flex gap-0.5">
        {legendItems.map((item, index) => (
          <div key={index} className="flex flex-col items-center">
            <div
              className="w-5 h-3 rounded-sm"
              style={{ backgroundColor: item.color }}
              title={item.description}
            />
            <span className="text-[8px] text-gray-500 dark:text-gray-400 mt-0.5">
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export const NDVIMapOverlay: React.FC<NDVIMapOverlayProps> = ({
  config,
  onOpacityChange,
  onDateChange,
  onRefresh,
  onToggle,
  isVisible = true,
  isLoading = false,
  availableDates = [],
}) => {
  const { t } = useTranslation()
  const [localOpacity, setLocalOpacity] = useState(config?.opacity || 0.7)
  const [showControls, setShowControls] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // AGC: Sync with config
  useEffect(() => {
    if (config?.opacity !== undefined) {
      setLocalOpacity(config.opacity)
    }
  }, [config?.opacity])

  // AGC: Handle opacity change
  const handleOpacityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    setLocalOpacity(value)
    onOpacityChange?.(value)
  }, [onOpacityChange])

  // AGC: Handle refresh
  const handleRefresh = useCallback(async () => {
    if (!onRefresh || isRefreshing) return
    
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setIsRefreshing(false)
    }
  }, [onRefresh, isRefreshing])

  // AGC: No config state - just show controls to load
  if (!config && !isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('fields.monitoring.ndviOverlay')}
          </h4>
        </div>
        <div className="flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="text-center">
            <Layers className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('fields.monitoring.noOverlayData')}
            </p>
            {onRefresh && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="px-3 py-1.5 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isRefreshing ? (
                  <RefreshCw className="w-4 h-4 animate-spin inline mr-1" />
                ) : (
                  <Layers className="w-4 h-4 inline mr-1" />
                )}
                {t('fields.monitoring.loadNdvi')}
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
      {/* AGC: Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('fields.monitoring.ndviOverlay')}
        </h4>
        
        <div className="flex items-center gap-2">
          {/* AGC: Toggle Visibility */}
          {onToggle && (
            <button
              onClick={() => onToggle(!isVisible)}
              className={`p-1.5 rounded-lg transition-colors ${
                isVisible
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
              title={isVisible ? t('fields.monitoring.hideOverlay') : t('fields.monitoring.showOverlay')}
            >
              {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
          )}

          {/* AGC: Refresh */}
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              title={t('common.refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* AGC: Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('fields.monitoring.loadingNdvi')}
            </p>
          </div>
        </div>
      )}

      {/* AGC: Overlay Controls */}
      {config && !isLoading && (
        <div className="space-y-4">
          {/* AGC: Preview Image */}
          <div className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
            <img
              src={config.url}
              alt="NDVI Overlay Preview"
              className="w-full h-32 object-cover"
              style={{ opacity: localOpacity }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
            <div className="absolute bottom-2 left-2">
              <NDVILegend />
            </div>
          </div>

          {/* AGC: Opacity Slider */}
          <div>
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>{t('fields.monitoring.opacity')}</span>
              <span>{Math.round(localOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={localOpacity}
              onChange={handleOpacityChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full appearance-none cursor-pointer accent-emerald-500"
            />
          </div>

          {/* AGC: Date Selector */}
          {availableDates.length > 0 && onDateChange && (
            <div>
              <label className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                <Calendar className="w-3 h-3" />
                {t('fields.monitoring.selectDate')}
              </label>
              <select
                value={config.date || ''}
                onChange={(e) => onDateChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {availableDates.map((date) => (
                  <option key={date} value={date}>
                    {new Date(date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* AGC: Date Display */}
          {config.date && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {t('fields.monitoring.overlayDate')}: {new Date(config.date).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default NDVIMapOverlay

